import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { updatePlan, getPlans, createIntent, setDefaultPaymentMethod } from '@app/services/plans';
import { getProfile } from '@app/services/user';
import { useAppDispatch, useAppSelector } from '@app/redux/hooks';
import { ActionTypes } from '@app/redux/types';
import { toast } from 'react-toastify';
import { Stripe, StripeElements } from '@stripe/stripe-js';

interface PlanDetails {
  name: string;
  price: string;
  priceId: string;
  planName: string;
}

export const useSubscriptionRenewalPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.auth?.user);
  const business = useAppSelector((state) => state.business);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [planDetails, setPlanDetails] = useState<PlanDetails | null>(null);
  const [clientSecret, setClientSecret] = useState<string>('');

  useEffect(() => {
    const initializeRenewal = async () => {
      try {
        if (!user || !business.selected) {
          navigate('/login');
          return;
        }

        const businessSubscription = user.subscriptions?.find(
          (sub) => sub.businessId === business.selected?.id
        );

        if (!businessSubscription) {
          toast.error('No subscription found for this business');
          navigate('/settings?tab=plan');
          return;
        }

        const currentPlanType = businessSubscription.plan?.type;
        if (!currentPlanType) {
          toast.error('No plan information found');
          navigate('/settings?tab=plan');
          return;
        }

        const [plansResponse, intentResponse] = await Promise.all([
          getPlans(),
          createIntent(),
        ]);

        const plans = plansResponse.data.plans;
        const apiPlan = plans.find((plan: any) => plan.type === currentPlanType);

        if (!apiPlan) {
          throw new Error('Plan not found');
        }

        setPlanDetails({
          name: `${currentPlanType.charAt(0) + currentPlanType.slice(1).toLowerCase()} Plan`,
          price: `$${apiPlan.price}`,
          priceId: apiPlan.priceId,
          planName: apiPlan.type,
        });

        setClientSecret(intentResponse.data.intent);
        setIsLoading(false);
      } catch (error) {
        console.error('Error initializing renewal:', error);
        toast.error('Error loading plan details');
        setIsLoading(false);
        navigate('/settings?tab=plan');
      }
    };

    initializeRenewal();
  }, [navigate, user, business.selected]);

  const handleStripeSuccess = async (stripe: Stripe, elements: StripeElements) => {
    setIsProcessing(true);
    try {
      if (!planDetails || !clientSecret) {
        throw new Error('Missing payment details');
      }

      const { error, setupIntent } = await stripe.confirmSetup({
        clientSecret,
        elements,
        confirmParams: {
          return_url: window.location.origin,
        },
        redirect: 'if_required',
      });

      if (error) {
        toast.error(error.message || 'Payment setup failed');
        setIsProcessing(false);
        return;
      }

      if (setupIntent?.payment_method) {
        const paymentMethodId = typeof setupIntent.payment_method === 'string'
          ? setupIntent.payment_method
          : setupIntent.payment_method.id;
        await setDefaultPaymentMethod(paymentMethodId);
      }

      await updatePlan(planDetails.priceId, true);

      try {
        const userResponse = await getProfile();
        dispatch({ type: ActionTypes.AUTH_SAVE, payload: userResponse?.data?.user });
        dispatch({
          type: ActionTypes.BUSINESS_LOAD_LIST,
          payload: userResponse?.data?.businesses,
        });
      } catch (error) {
        console.error('Error refreshing user data:', error);
      }

      toast.success('Subscription renewed successfully!');

      setTimeout(() => {
        navigate('/');
      }, 1000);
    } catch (error) {
      console.error('Error processing renewal:', error);
      toast.error('Error renewing subscription. Please try again.');
      setIsProcessing(false);
    }
  };

  return {
    planDetails,
    clientSecret,
    handleStripeSuccess,
    isProcessing,
    isLoading,
  };
};
